import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { sdk } from "@farcaster/frame-sdk";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FarcasterUser {
  fid: number;
  username?: string;
  displayName?: string;
  pfpUrl?: string;
}

interface FarcasterContextValue {
  user: FarcasterUser | null;
  isInApp: boolean;
  isLoading: boolean;
  requestMicAndCamera: () => Promise<boolean>;
  composeCast: (text: string, embedUrl?: string) => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const FarcasterContext = createContext<FarcasterContextValue>({
  user: null,
  isInApp: false,
  isLoading: true,
  requestMicAndCamera: async () => false,
  composeCast: async () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export const FarcasterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<FarcasterUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInApp, setIsInApp] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        // Signal the Farcaster client the app is ready
        await sdk.actions.ready();
        const context = await sdk.context;
        if (context?.user) {
          setUser({
            fid: context.user.fid,
            username: context.user.username,
            displayName: context.user.displayName,
            pfpUrl: context.user.pfpUrl,
          });
          setIsInApp(true);
        }
      } catch {
        // Not running inside a Farcaster client – graceful degradation
        setIsInApp(false);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  /**
   * Request camera + microphone access through the Farcaster client.
   * Falls back to the standard browser API outside Farcaster.
   * Returns true if access was granted.
   */
  const requestMicAndCamera = useCallback(async (): Promise<boolean> => {
    if (isInApp) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ctx = (await sdk.context) as any;
      const platformType: string | null = ctx?.client?.platformType ?? null;
      const hasStoredAccess = Boolean(ctx?.features?.cameraAndMicrophoneAccess);

      if (platformType !== "mobile" || hasStoredAccess) {
        return true;
      }

      try {
        // Farcaster SDK permission request (resolves after user responds)
        await (sdk.actions as any).requestCameraAndMicrophoneAccess();
        return true;
      } catch {
        return false;
      }
    }

    // Web mini apps and normal browsers still rely on the browser media prompt.
    return true;
  }, [isInApp]);

  /**
   * Open the Farcaster cast composer pre-filled with text and an optional URL embed.
   */
  const composeCast = useCallback(async (text: string, embedUrl?: string) => {
    try {
      await sdk.actions.composeCast({
        text,
        embeds: embedUrl ? [embedUrl as `https://${string}`] : [],
      });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.error("composeCast failed:", e);
      }
    }
  }, []);

  return (
    <FarcasterContext.Provider
      value={{ user, isInApp, isLoading, requestMicAndCamera, composeCast }}
    >
      {children}
    </FarcasterContext.Provider>
  );
};

export const useFarcaster = () => useContext(FarcasterContext);
